import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import useNextPublicChallenge from '../../hooks/useNextPublicChallenge';
import Reveal from '../../components/course/Reveal';
import { AI_TRADING_CHALLENGE_NAME, toChallengeSlug } from './challengePageUtils';
import '../courses/CoursePage.css';
import './ChallengePage.css';

/**
 * /challenges entry point.
 *
 * WHY: we only ever run one challenge at a time, so there is no index page.
 * This resolves the next public run from the education DB and redirects to
 * its slug page (keeping query params so UTM/promo attribution survives).
 * When nothing is scheduled it shows a small "announced soon" state that
 * still routes visitors toward the bootcamp and the last run's recordings.
 */
const NextChallengePage = () => {
  const { challenge, isLoading } = useNextPublicChallenge();
  const location = useLocation();
  const [lastRun, setLastRun] = useState(null);

  // Only needed for the fallback state: find the most recent past public run
  // so its recordings stay reachable after a challenge ends.
  useEffect(() => {
    if (isLoading || challenge) return undefined;
    let mounted = true;
    const API_URL = getApiBaseUrl() || 'https://api.botspot.trade';
    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/courses/session-groups?active=true&registrationAccess=public&limit=50`,
        );
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json();
        const groups = Array.isArray(data) ? data : (data.sessionGroups || data.data || []);
        const past = groups
          .filter((g) => g?.scheduledAt && new Date(g.scheduledAt).getTime() <= Date.now())
          .sort((a, b) => new Date(b.scheduledAt) - new Date(a.scheduledAt))[0] || null;
        if (mounted && past) {
          setLastRun({ name: past.name, slug: toChallengeSlug(past.name) });
        }
      } catch {
        /* Fallback still offers the bootcamp CTA without a recordings link. */
      }
    })();
    return () => { mounted = false; };
  }, [isLoading, challenge]);

  if (isLoading) {
    return (
      <div className="course-page challenge-page" data-testid="challenge-gate-loading">
        <div className="chp-gate-loading" role="status" aria-live="polite">
          Finding the next challenge...
        </div>
      </div>
    );
  }

  if (challenge?.path) {
    return (
      <Navigate
        to={`${challenge.path}${location.search}${location.hash}`}
        replace
      />
    );
  }

  return (
    <div className="course-page challenge-page" data-testid="challenge-fallback">
      <Helmet>
        <title>{AI_TRADING_CHALLENGE_NAME} | BotSpot</title>
        <meta
          name="description"
          content="A free seven-day live challenge: set up an AI trading workflow with Claude, Codex, or BotSpot alongside Rob."
        />
      </Helmet>
      <header className="chp-hero-grid chp-hero-grid--single">
        <div>
          <Reveal>
            <p className="chp-live-badge">
              <span className="chp-live-dot" aria-hidden="true" />
              Free · Live with Rob
            </p>
          </Reveal>
          <Reveal delay={60}>
            <h1 className="chp-hero-title">The next {AI_TRADING_CHALLENGE_NAME} is being scheduled</h1>
          </Reveal>
          <Reveal delay={120}>
            <p className="chp-hero-sub">
              Each challenge includes a live kickoff, one continuing AI trading workflow,
              light tips during the week, and a live wrap-up with Rob. New dates are announced by email first.
            </p>
          </Reveal>
          <Reveal delay={180}>
            <div className="chp-fallback-actions">
              <Link className="cp-btn-primary" to="/courses/ai-trading-bootcamp" data-testid="challenge-fallback-cta">
                See the AI Trading Bootcamp
              </Link>
              {lastRun ? (
                <Link className="chp-fallback-recordings" to={`/challenges/${lastRun.slug}`}>
                  Watch the last challenge recordings
                </Link>
              ) : null}
            </div>
          </Reveal>
        </div>
      </header>
    </div>
  );
};

export default NextChallengePage;
